import React, { useEffect, useCallback, useLayoutEffect, useRef } from 'react';
import { useVideo } from 'customHooks/mediaElement';
import RadicalInfoBar from './RadicalInfoBar';
import WordsVideoControl from './WordsVideoControl';
import WordsMovieButtonGroup from './WordsMovieButtonGroup';
import WordsMeaningButtonGroup from './WordsMeaningButtonGroup';
import AudioButton from 'components/common/AudioButton';
import Icon from 'components/Icon';
import GridButtonGroup from './GridButtonGroup';
import useSetState from 'customHooks/setState';
import { mediaType } from 'constants/mediaType';
import { isImageExtension } from 'util/helper';
import { useStore, StoreTypes } from 'context';
import { API } from 'api';
import Bopomofo from 'components/common/Bopomofo';
import { EventBus, EventBusType } from 'events/EventBus';
import { ExtendedContentEvent, ReaderToolsEvent } from 'events/EventTypes';
import { LineType } from 'constants/wordGrid';
import classnames from 'classnames';
import { PinchPanContainer } from 'components/PinchPanContainer';
import * as actionTypes from 'constants/actionTypes';
import styles from './index.module.scss';
import { getOSAndBrowser } from 'util/getOs';
import Repository from 'repositories/Repository';
import { stat } from 'fs';

import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { FontSizeType } from 'constants/fontSizeType';

const { ExtendedResourceRepository } = Repository;

const videoPointSetControlStateType = {
  PLAY: 'play',
  PAUSE: 'pause',
  NEXT: 'next',
  REPEAT: 'repeat',
  PREVIOUS: 'previous'
};


const EXTENDTEACH_WORDING_MAP = {
  meaningSet: '字義教學',
  wordsSet: '語詞教學',
  idiomSet: '生字成語',
  glyphSet: '字音字形'
};

const MOVIE_BUTTON_WORDING_MAP = ['筆順教學', '筆畫教學', '綜合教學', '書寫提醒'];
const WordLearningGridComponent = ({
  data,
  wordIndex,
  goToNextWordIndex,
  onRadicalClick = () => { },
  isPausePlayAllWords,
  setPausePlayAllWords
}) => {
  const [{ bookId }] = useStore(StoreTypes.books);
  const [{ isShowSubContent }, wordGridDispatch] = useStore(StoreTypes.wordGrid);
  const [{ wordLearningGrid: {
    playbackRate
  }, fontSize }, extendedContentDispatch] = useStore(StoreTypes.extendedContent);

  const [
    {
      Name: word,
      RadicalWord: radical,
      RadicalNumber: radicalNumber,
      AllNumber: allNumber,
      RestNumber: restNumber,
      Phonetic: phonetic,
      WordMovieSet,
      MeaningSet,
      WordsSet,
      IdiomSet,
      GlyphSet
    },
    setDataState
  ] = useSetState(data);

  const [
    {
      videoAutoPlay,
      videoSrc,
      videoPointSet,
      currentPointSetIndex,
      currentVideoIndex,
      isZoomVideo,
      currentWordsMeaningIndex,
      isPlayAllWords,
      gridValue,
      isSwitching,
      videoPointSetControlState
    },
    setState
  ] = useSetState({
    videoAutoPlay: false,
    videoSrc: WordMovieSet[0].Path || '',
    videoPointSet: WordMovieSet[0].MoviePointSet || [],
    currentPointSetIndex: 0,
    currentVideoIndex: 0,
    isZoomVideo: false,
    currentWordsMeaningIndex: null,
    isPlayAllWords: false,
    gridValue: LineType.Dotted,
    isSwitching: false,
    videoPointSetControlState: videoPointSetControlStateType.PAUSE
  });

  // 切換生字時，前一個字的影片可能還在播放，state.isPlaying 會殘留 true
  // 用來擋掉切字瞬間的 point index 誤加（避免下一筆從第二畫開始）
  const isWordSwitchingRef = useRef(false);

  // NBK-1612: 單筆播放的暫停計時器(見下方 scheduleStrokePause)
  const pointPauseTimerRef = useRef(null);

  const {
    element: video,
    state,
    controls,
    elementRef
  } = useVideo(
    <video
      src={`${ExtendedResourceRepository.getDataSrc({
        bookId,
        pathName: videoSrc
      })}#t=0.1`}
      autoPlay={videoAutoPlay}
      playsInline
      crossOrigin="anonymous"
      preload="metadata"
    />
  );

  const clearPointPauseTimer = useCallback(() => {
    if (pointPauseTimerRef.current) {
      clearTimeout(pointPauseTimerRef.current);
      pointPauseTimerRef.current = null;
    }
  }, []);

  // NBK-1612: 依「剩餘媒體時間 ÷ 倍率」排程單筆暫停，取代原本用 timeupdate 輪詢比對 endPointTime 的做法。
  // timeupdate 約每 250ms 才更新一次，暫停會慢一拍，倍率越高 overshoot 越明顯(6x=2 倍速會吃到下一筆開頭)；
  // 這裡用 setTimeout 直接算到點，回呼內再讀真實 currentTime 反覆校正，緩衝/延遲啟播也不會提早停。
  const scheduleStrokePause = useCallback(endTime => {
    clearPointPauseTimer();
    const tick = () => {
      const el = elementRef.current;
      if (!el || el.paused) {          // 已卸載或被外部暫停(手動/切字)→ 不再排程
        pointPauseTimerRef.current = null;
        return;
      }
      const rate = el.playbackRate || 1;
      const remainMs = ((endTime - el.currentTime) / rate) * 1000;
      if (remainMs > 0) {
        pointPauseTimerRef.current = setTimeout(tick, remainMs);
        return;
      }
      controls.pause();
      // controls.pause() 可能因 useVideo 內部 lockPlay(剛 play() 的 promise 還沒 settle)而被忽略；
      // 尤其「播到最後一筆後再按下一筆」會 wrap 回起點、需立即暫停卻剛好撞上 lockPlay。
      // 若沒真的停下就短暫重試，等 lockPlay 解除，避免整段影片被放著播完。
      if (!el.paused) {
        pointPauseTimerRef.current = setTimeout(tick, 32);
        return;
      }
      pointPauseTimerRef.current = null;
    };
    tick();
  }, [clearPointPauseTimer, controls, elementRef]);

  // 卸載時清掉計時器，避免非同步 pause 打到已卸載的元件
  useEffect(() => () => clearPointPauseTimer(), [clearPointPauseTimer]);


  // 20250227 在 ios 手機上 video 的寬度，沒辦法即時 render 正確，所以故意利用 fontSize 來觸發 re-render
  useEffect(() => {
    if (isShowSubContent) return;
    if (state.isLoaded) {
      extendedContentDispatch({
        type: actionTypes.SET_EXTENDED_CONTENT_FONT_SIZE,
        payload: {
          fontSize: FontSizeType.Medium,
        },
      });
    } else {
      extendedContentDispatch({
        type: actionTypes.SET_EXTENDED_CONTENT_FONT_SIZE,
        payload: {
          fontSize: FontSizeType.Large,
        },
      });
    }
  }, [state, isShowSubContent])

  const repeatButtonClickHandler = () => {
    setState({ videoPointSetControlState: videoPointSetControlStateType.REPEAT });
    const lastPoint = Math.max(currentPointSetIndex - 1, 0);
    controls.seek(videoPointSet[lastPoint]);
    controls.play();
    // 重播單筆同樣改用排程暫停，避免高倍速 overshoot
    scheduleStrokePause(videoPointSet[currentPointSetIndex]);
  }

  useLayoutEffect(() => {
    // 需求為 延遲播放不跟音檔重疊
    setTimeout(() => {
      !isShowSubContent && !isPausePlayAllWords && isPlayAllWords && controls.play();
      setPausePlayAllWords(false)
    }, 2000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controls, isPlayAllWords, videoSrc]);

  // 隨著播放時間 更新 point 的 index
  useEffect(() => {
    // for normal play
    if (
      videoPointSet.length < 1 ||
      !videoPointSetControlState ||
      (videoPointSetControlState !== videoPointSetControlStateType.PLAY &&
        videoPointSetControlState !== videoPointSetControlStateType.PAUSE)
    ) return;
    // 切字後，忽略前一個字殘留 isPlaying 造成的 index 誤加；
    // 等到 isPlaying 真的變 false（舊影片停下）再恢復追蹤
    if (isWordSwitchingRef.current) {
      if (!state.isPlaying) {
        isWordSwitchingRef.current = false;
      }
      return;
    }
    let pointIndex = currentPointSetIndex;
    let pointTime = videoPointSet[currentPointSetIndex];
    if (state.isPlaying && state.time >= pointTime) {
      pointIndex = Math.min(pointIndex + 1, videoPointSet.length - 1);
      setState({
        currentPointSetIndex: pointIndex,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.time,
    state.isPlaying,
    currentPointSetIndex,
    videoPointSet,
    videoPointSetControlState,
    setState
  ]);

  // 自動播放下一個字
  useEffect(() => {
    if (!state.duration) return;
    if (state.time >= state.duration && isPlayAllWords && !isSwitching) {
      setState({ isSwitching: true });
      goToNextWordIndex();
      // TODO: should implement useVideo onload listener to handle this
      setTimeout(() => {
        setState({ isSwitching: false });
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.time,
    state.duration,
    wordIndex,
    isPlayAllWords,
    goToNextWordIndex,
    isSwitching,
    setState
  ]);

  const wordMovieButtonHandler = ({ index, value }) => {
    setState({
      videoAutoPlay: false,
      videoSrc: value,
      currentVideoIndex: index,
      videoPointSet: WordMovieSet[index].MoviePointSet,
      // NBK-1612: 切換教學方式後影片其實停在起點(尚未播放任何一畫)，
      // 這裡若預設成 1，第一次點「下一筆」會被 pointSetChangeHandler 加成 2 而跳過第 1 畫、
      // 造成 1、2 筆連播。改回 0 讓「下一筆」從第 1 畫開始逐筆播；
      // 第一畫的顯示已由影片網址的 #t=0.1 負責(見上方 video src)，不會回歸 #13204。
      currentPointSetIndex: 0,
      videoPointSetControlState: videoPointSetControlStateType.PLAY
    });
  };

  const meaningButtonHandler = useCallback(({ type }) => {
    controls.pause();

    wordGridDispatch({
      type: actionTypes.SET_SUBCONTENT_SHOW,
      payload: {
        isShowSubContent: true,
        extensionType: type
      }
    })

  }, [controls, wordGridDispatch]);

  const pointSetChangeHandler = useCallback(
    type => {
      let pointIndex = currentPointSetIndex;
      if (type === mediaType.PREVIOUS) {
        pointIndex = Math.max(pointIndex - 1, 0);
      } else {
        pointIndex = (pointIndex + 1 >= videoPointSet.length) ? 0 : pointIndex + 1;
      }

      controls.seek(videoPointSet[Math.max(pointIndex - 1, 0)]);
      controls.play();
      // 播到「這一筆終點秒數」就停，改用排程暫停消除 timeupdate 取樣延遲造成的 overshoot
      scheduleStrokePause(videoPointSet[pointIndex]);
      setState({
        currentPointSetIndex: pointIndex,
        videoPointSetControlState: type === mediaType.PREVIOUS ? videoPointSetControlStateType.PREVIOUS : videoPointSetControlStateType.NEXT
      });
    },
    [currentPointSetIndex, controls, videoPointSet, setState, scheduleStrokePause]
  );

  const playbackRateChangeHandler = playbackRate => {
    EventBus.emit({
      event: ExtendedContentEvent.SetWordLearningGridPlaybackRateEvent,
      payload: { playbackRate }
    })
  }

  useEffect(() => {
    controls.playbackRate(playbackRate);
  }, [controls, playbackRate, videoSrc])

  const play = useCallback(() => {
    // 手動播放/暫停時取消單筆排程，避免計時器稍後又把影片暫停
    clearPointPauseTimer();
    state.isPlaying ? controls.pause() : controls.play();
    setState({ videoPointSetControlState: videoPointSetControlStateType[state.isPlaying ? 'PLAY' : 'PAUSE'] });
  }, [controls, setState, state.isPlaying, clearPointPauseTimer]);

  useEffect(() => {

    const extensionData = {
      meaningSet: data.MeaningSet,
      wordsSet: data.WordsSet,
      idiomSet: data.IdiomSet,
      glyphSet: data.GlyphSet,
    };
    setDataState(data);

    // 切字時停掉前一個字仍在播放的影片，並標記切換中，
    // 避免殘留的播放狀態把 currentPointSetIndex 從 0 誤加成 1
    isWordSwitchingRef.current = true;
    clearPointPauseTimer();
    controls.pause();

    let tempCurrentVideoIndex = currentVideoIndex;
    if(currentVideoIndex > WordMovieSet.length - 1) {
      tempCurrentVideoIndex = 0;
    }

    setState({
      videoAutoPlay: false,
      videoSrc: WordMovieSet[tempCurrentVideoIndex].Path || '',
      videoPointSet: WordMovieSet[tempCurrentVideoIndex].MoviePointSet || [],
      currentPointSetIndex: 0,
      currentVideoIndex: tempCurrentVideoIndex,
      videoPointSetControlState: videoPointSetControlStateType.PAUSE,
      audioSrc: '',
      currentWordsMeaningIndex
    });
    wordGridDispatch({
      type: actionTypes.SET_EXTENSION_DATA,
      payload: {
        extensionData
      }
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [WordMovieSet, setState, data, setDataState, isPlayAllWords, wordIndex]);

  const onPlayAllWordsClick = status => {
    setState({ isPlayAllWords: status });
  };

  const onRadicalClickHandler = useCallback(radicalPinyin => {
    controls.pause();
    controls.seek(0);
    onRadicalClick(radicalPinyin);
  }, [controls, onRadicalClick])

  const toggleZoomVideoHandler = useCallback(() => {
    controls.pause();
    controls.seek(0);
    setState({ isZoomVideo: !isZoomVideo })
  }, [controls, isZoomVideo, setState])

  return (
    <>
      <div className={classnames(styles.center, styles.images)}>
        <div className={classnames(styles.videoContainer, {
          [styles.fullSize]: isZoomVideo
        })}>
          <div className={styles.gridWrapper}>
            <div className={styles.grid}>
              <div className={classnames(styles.video, [styles[gridValue]])}>
                {video}
              </div>
              {phonetic && isImageExtension(phonetic) ? (
                <div className={styles.phonetic}>
                  <img
                    src={ExtendedResourceRepository.getDataSrc({
                      bookId,
                      pathName: phonetic
                    })}
                    alt=""
                  />
                </div>
              ) : (<Bopomofo className={styles.bopomofo} zhuyin={phonetic} />)}
              <div className={styles.zoomIcon} onClick={toggleZoomVideoHandler}>
                <Icon type="text" name={isZoomVideo ? 'searchMinus' : 'searchPlus'} />
              </div>
            </div>
          </div>
        </div>
        {
          !isZoomVideo && (
            <RadicalInfoBar
              radical={radical}
              radicalNumber={radicalNumber}
              allNumber={allNumber}
              restNumber={restNumber}
              onRadicalClick={onRadicalClickHandler}
            />
          )
        }
      </div>
      <div className={styles.right}>
        <section>
          <div className={styles.title}>
            <span>教學方式</span>
          </div>
          <WordsMovieButtonGroup
            isPlaying={state.isPlaying}
            play={play}
            wordingMap={MOVIE_BUTTON_WORDING_MAP}
            data={WordMovieSet}
            activeIndex={currentVideoIndex}
            onChange={wordMovieButtonHandler}
          />
          <WordsVideoControl
            previousButtonClick={() =>
              pointSetChangeHandler(mediaType.PREVIOUS)
            }
            previousButtonDisabled={
              currentPointSetIndex === 0 || videoPointSet.length < 1
            }
            nextButtonClick={() => pointSetChangeHandler(mediaType.NEXT)}
            nextButtonDisabled={
              videoPointSet.length < 1
            }
            repeatDisabled={state.isPlaying || currentPointSetIndex < 1}
            repeatButtonClick={() => repeatButtonClickHandler()}
            playbackRate={playbackRate}
            onChangePlaybackRate={playbackRateChangeHandler}
            onPlayAllWordsClick={onPlayAllWordsClick}
          />
        </section>
        <section className={styles.extendSection}>
          <div className={styles.title}>
            <span>延伸教學</span>
          </div>
          <WordsMeaningButtonGroup
            className={styles.extendTeach}
            onChange={meaningButtonHandler}
            wordingMap={EXTENDTEACH_WORDING_MAP}
            data={{
              meaningSet: MeaningSet,
              wordsSet: WordsSet,
              idiomSet: IdiomSet,
              glyphSet: GlyphSet
            }}
          />
        </section>
        <section className={styles.gridSection}>
          <div className={styles.title}>
            <span>十字格線</span>
          </div>
          <GridButtonGroup onChange={value => setState({ gridValue: value })} value={gridValue} />
        </section>
      </div>
    </>
  );
};

const WordLearningGrid = ({ dataList = [], defaultWord }) => {
  const [{ bookId }] = useStore(StoreTypes.books);
  const [{ word, isShowSubContent, isShowRadicalContent, isShowRadicalProgressModal }, wordGridDispatch] = useStore(StoreTypes.wordGrid);
  const [
    { list, currentWordIndex, isAudioAutoPlay, isPausePlayAllWords },
    setState
  ] = useSetState({
    list: [],
    currentWordIndex: 0,
    isAudioAutoPlay: true,
    isPausePlayAllWords: false
  });

  useEffect(() => {
    Promise.all(
      dataList.map(word =>
        ExtendedResourceRepository.getJSONContent({
          bookId,
          pathName: word.Path
        })
      )
    ).then(list => {
      wordGridDispatch({
        type: actionTypes.SET_WORDS_DATA,
        payload: {
          data: list
        }
      })
      setState({ list: list.filter(item => item) });
    });
  }, [bookId, dataList, setState, wordGridDispatch]);

  const goToNextWordIndex = useCallback(() => {
    const maxIndex = dataList.length - 1;
    const nextIndex = currentWordIndex + 1;
    if (maxIndex >= nextIndex) {
      const wordIndex = Math.min(nextIndex, maxIndex);
      wordGridDispatch({
        type: actionTypes.SET_WORDS_VALUE,
        payload: {
          word: list[wordIndex].Name
        }
      })
    }
  }, [dataList.length, currentWordIndex, wordGridDispatch, list]);

  const onRadicalClick = useCallback(
    async radicalPinyin => {
      const url = await ExtendedResourceRepository.getDataSrc({
        bookId,
        pathName: `word/部首/${radicalPinyin}/${radicalPinyin}.json`
      });
      const data = await API.getJSON(url);
      if (data) {
        // setState({ isRadicalShow: true, radicalData: data });

        wordGridDispatch({
          type: actionTypes.SET_RADICAL_CONTENT_SHOW,
          payload: {
            isShowRadicalContent: true,
            radicalData: data
          }
        })
      }
    },
    [bookId, wordGridDispatch]
  );

  useEffect(() => {
    const tempWord = word || defaultWord;
    if (!tempWord) return;
    const index = dataList.findIndex(item => {
      return item.Name === tempWord;
    });
    setState({ currentWordIndex: index > -1 ? index : 0 });
  }, [dataList, setState, word, defaultWord]);

  useEffect(() => {
    const eventBusType = EventBusType.ExtendedContent;
    EventBus.emit({
      eventBusType,
      event: ReaderToolsEvent.ClickEraseAllEvent
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWordIndex, isShowSubContent, isShowRadicalContent, isShowRadicalProgressModal])

  useEffect(() => {
    if (isShowSubContent) {
      setState({ isAudioAutoPlay: false })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShowSubContent])

  // 顯示部首內容時關閉側邊清單的自動播放，避免搶奪播放權
  useEffect(() => {
    if (isShowRadicalContent) {
      setState({ isAudioAutoPlay: false })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShowRadicalContent])

  const setPausePlayAllWords = status => {
    setState({ isPausePlayAllWords: status })
  }

  const wordClickHandler = word => {
    wordGridDispatch({
      type: actionTypes.SET_WORDS_VALUE,
      payload: {
        word
      }
    })

    setPausePlayAllWords(true)
  }


  return (
    <>
      <PinchPanContainer>
        <div
          className={classnames(styles.wordLearningGrid, {
            [styles.notMac]:
              getOSAndBrowser() !== "Mac",
          })}
        >
          <div className={styles.left}>
            <div className={classnames(styles.wordList, styles.connected)}>
              {dataList.map((item, index) => (
                <div
                  key={item.Name}
                  className={classnames(
                    styles.word,
                    {
                      [styles.active]:
                        index ===
                        currentWordIndex,
                      [styles.optional]: !!item.Optional,
                    }
                  )}
                  onClick={() =>
                    wordClickHandler(item.Name)
                  }
                >
                  {list[index] && (
                    <AudioButton
                      autoPlay={
                        index ===
                        currentWordIndex &&
                        isAudioAutoPlay
                      }
                      src={`${ExtendedResourceRepository.getDataSrc(
                        {
                          bookId,
                          pathName:
                            list[index]
                              .WordAudioPath,
                        }
                      )}`}
                    >
                      {item.Name}
                    </AudioButton>
                  )}
                </div>
              ))}
            </div>
          </div>
          {list && list.length > 0 && (
            <WordLearningGridComponent
              data={list[currentWordIndex]}
              wordIndex={currentWordIndex}
              goToNextWordIndex={goToNextWordIndex}
              onRadicalClick={onRadicalClick}
              isPausePlayAllWords={
                isPausePlayAllWords
              }
              setPausePlayAllWords={
                setPausePlayAllWords
              }
            />
          )}
        </div>
      </PinchPanContainer>
    </>
  );
};

export default WordLearningGrid;
